import React, { useEffect, useState, useMemo } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Input, Switch, Space, App } from 'antd';
import { LockOutlined, UnlockOutlined, PlusOutlined, UserAddOutlined, ExclamationCircleOutlined, EditOutlined, TeamOutlined, DeleteOutlined, KeyOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@zero/auth-client';
import { createAuthApi, PasswordStrengthIndicator, usePasswordStrength, RoleAssignmentDialog } from '@zero/auth-admin-ui';
import { useGridPageSize, GRID_PAGE_SIZE_OPTIONS } from '../../hooks/useGridPageSize.js';

const ROLES_MODAL_Z      = 1000;
const ROLES_ASSIGN_DLG_Z = 1100;   // > outer → inner luôn ở trên

const MIN_PW = 12;

export function UsersPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const { has } = usePermissions();
  // Khoá mật khẩu dùng chung quyền với đặt lại mật khẩu — cùng mức tác động lên tài khoản.
  const canLockPw = has?.('Auth.User.ResetPassword');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useGridPageSize('users', 25);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createForm] = Form.useForm();
  const newPw = Form.useWatch('InitialPassword', createForm) ?? '';
  const { valid: pwValid } = usePasswordStrength(newPw, MIN_PW);
  const [editing, setEditing]     = useState(null);   // null | user object
  const [pwUser, setPwUser]       = useState(null);   // user dang dat lai mat khau
  const [pwSaving, setPwSaving]   = useState(false);
  const [pwForm]                  = Form.useForm();
  const [editBusy, setEditBusy]   = useState(false);
  const [editForm]  = Form.useForm();

  // Roles management dialog (outer) + RoleAssignmentDialog (inner)
  const [rolesUser, setRolesUser]           = useState(null);     // null | user object
  const [userRoles, setUserRoles]           = useState([]);
  const [userRolesLoading, setUserRolesLoading] = useState(false);
  const [rolesCatalog, setRolesCatalog]     = useState([]);
  const [assignOpen, setAssignOpen]         = useState(false);
  // Danh sach tau cho pham vi "Vessel". TRUOC DAY truyen units={[]} cung nen chon
  // "Tàu" khong ra gi de chon. Scope value la ShipGuid (hien phap: KHONG dung ShipID/ShipCode).
  const [ships, setShips]                   = useState([]);

  // GUID tàu -> tên tàu (phạm vi Vessel neo bằng ShipGuid, KHÔNG phải số) để cột "Phạm vi"
  // hiện tên tàu thay vì GUID; RoleID -> mô tả role để dán kèm sau tên role.
  const shipNameById = useMemo(() => {
    const m = new Map();
    for (const s of ships) m.set(String(s.ID).toLowerCase(), s.Name);
    return m;
  }, [ships]);
  const roleDescById = useMemo(() => {
    const m = new Map();
    for (const r of rolesCatalog) m.set(r.RoleID, r.Description);
    return m;
  }, [rolesCatalog]);
  // Nhãn role trong hộp gán = "Tên — Mô tả" (bám Haian) để dễ chọn đúng vai trò.
  const rolesForAssign = useMemo(
    () => rolesCatalog.map((r) => (r.Description ? { ...r, Name: `${r.Name} — ${r.Description}` } : r)),
    [rolesCatalog],
  );

  const submitCreate = async (values) => {
    setCreateBusy(true);
    try {
      await api.createUser({
        Username:        values.Username,
        Email:           values.Email || null,
        FullName:        values.FullName || null,
        InitialPassword: values.InitialPassword,
      });
      message.success(t('user.createdAt') + ' ' + new Date().toLocaleTimeString());
      setCreateOpen(false);
      createForm.resetFields();
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setCreateBusy(false);
    }
  };

  const refresh = () => {
    setLoading(true);
    api.listUsers({ offset: 0, limit: 100 })
      .then((r) => setUsers(r.data?.Data?.Items ?? []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const openEdit = (u) => {
    editForm.setFieldsValue({
      FullName: u.FullName ?? '',
      Email:    u.Email    ?? '',
    });
    setEditing(u);
  };
  const openRolesDialog = async (u) => {
    setRolesUser(u);
    loadShips();                 // để cột "Phạm vi" của bảng vai trò dịch được GUID -> tên tàu
    setUserRolesLoading(true);
    try {
      const [rolesRes, catalogRes] = await Promise.all([
        api.listUserRoles(u.UserID),
        rolesCatalog.length === 0 ? api.listRoles({ offset: 0, limit: 200 }) : Promise.resolve(null),
      ]);
      setUserRoles(rolesRes.data?.Data ?? []);
      if (catalogRes) setRolesCatalog(catalogRes.data?.Data?.Items ?? []);
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setUserRolesLoading(false);
    }
  };

  const refreshUserRoles = async () => {
    if (!rolesUser) return;
    setUserRolesLoading(true);
    try {
      const r = await api.listUserRoles(rolesUser.UserID);
      setUserRoles(r.data?.Data ?? []);
    } finally {
      setUserRolesLoading(false);
    }
  };

  const openResetPw = (u) => { pwForm.resetFields(); setPwUser(u); };

  // Khoá mật khẩu: backend đặt mật khẩu ngẫu nhiên 32 ký tự và KHÔNG trả về, nên trên thực tế
  // tài khoản chỉ còn đăng nhập bằng Google. Backend tự chặn nếu Google toàn hệ thống đang tắt
  // hoặc tài khoản chưa có email — ở đây chỉ cần hiện lại thông báo lỗi của nó.
  const doLockPass = (u) => modal.confirm({
    title:   t('user.pw.lockConfirmTitle'),
    icon:    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
    content: t('user.pw.lockConfirmContent', { username: u.Username, email: u.Email || '—' }),
    okText:  t('user.pw.lock'),
    okButtonProps: { danger: true },
    cancelText: t('common.cancel'),
    onOk: async () => {
      try {
        await axios.post(`/api/account-admin/users/${u.UserID}/lock-pass`);
        message.success(t('user.pw.lockDone', { username: u.Username }));
        refresh();
      } catch (err) {
        message.error(err?.response?.data?.Message ?? err.message);
        throw err;   // giữ hộp thoại mở để admin đọc được lỗi
      }
    },
  });

  const submitResetPw = async ({ NewPassword }) => {
    setPwSaving(true);
    try {
      await api.resetUserPassword(pwUser.UserID, NewPassword);
      // Doi mat khau lam MOI SecurityStamp -> moi phien dang nhap cu cua user do bi da ra.
      message.success(t('user.pw.done', { username: pwUser.Username }));
      setPwUser(null);
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setPwSaving(false);
    }
  };

  const loadShips = async () => {
    if (ships.length) return;
    try {
      const r = await axios.get('/api/master-data/ships');
      // BAY TEN TRUONG: ShipDto goi la `ShipID` nhung GIA TRI la ShipGuid
      // (xem ShipDto.cs: "ShipID = DM_Ship.ShipGuid (PK, bat bien)").
      // Lay nham `ShipGuid` -> undefined -> option khong the chon duoc, ma nhan van hien
      // binh thuong nen rat kho thay.
      setShips((r.data?.Data ?? [])
        .filter((s2) => s2.ShipID)
        .map((s2) => ({ ID: s2.ShipID, Name: s2.ShipName || s2.ShipCode })));
    } catch (err) {
      // Thieu quyen FMS.MAST.Ship.View thi chi mat danh sach, khong chan viec gan vai tro khac.
      message.warning(err?.response?.data?.Message ?? err.message);
    }
  };

  const submitAssign = async (payload) => {
    try {
      await api.assignUserRole(rolesUser.UserID, {
        RoleID:       payload.roleID,
        ScopeType:    payload.scopeType,
        // ScopeItems = dang CHUOI, BE uu tien dung no (ShipGuid). ScopeValues (int)
        // giu rong vi FMS neo tau bang GUID chu khong phai so.
        ScopeItems:   (payload.scopeIDs ?? []).map(String),
        ScopeValues:  [],
        Reason:       payload.reason ?? null,
        ExpiresAtUTC: payload.expiresAtUTC ?? null,
      });
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
      refreshUserRoles();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    }
  };

  const removeRole = (ur) => modal.confirm({
    title:   t('user.roles.removeConfirmTitle'),
    content: t('user.roles.removeConfirmContent', { role: ur.RoleName }),
    icon:    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
    okText:  t('common.delete'),
    okButtonProps: { danger: true },
    cancelText: t('common.cancel'),
    zIndex:  ROLES_ASSIGN_DLG_Z + 50,   // confirm trên cả outer Modal
    onOk:    async () => {
      try {
        await api.unassignUserRole(rolesUser.UserID, ur.UserRoleID);
        refreshUserRoles();
      } catch (err) {
        message.error(err?.response?.data?.Message ?? err.message);
      }
    },
  });

  const submitEdit = async (values) => {
    setEditBusy(true);
    try {
      await api.updateUser(editing.UserID, {
        Email:    values.Email    || null,
        FullName: values.FullName || null,
      });
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
      setEditing(null);
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setEditBusy(false);
    }
  };

  const columns = [
    { title: t('common.id'),       dataIndex: 'UserID',    width: 80 },
    { title: t('user.username'),   dataIndex: 'Username',  render: (v) => <strong>{v}</strong> },
    { title: t('user.fullName'),   dataIndex: 'FullName',  render: (v) => v ?? <span style={{ opacity: 0.4 }}>–</span> },
    { title: t('user.email'),      dataIndex: 'Email' },
    { title: t('user.active'),     dataIndex: 'IsActive',  width: 100, render: (v) => v
        ? <Tag color="success">{t('common.active')}</Tag>
        : <Tag color="error">{t('common.locked')}</Tag> },
    { title: '2FA',                dataIndex: 'TwoFactorEnabled', width: 80, render: (v) => v ? <Tag>{t('common.yes')}</Tag> : '-' },
    { title: t('common.actions'), key: 'a', width: 280, render: (_, u) => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(u)}>{t('common.edit')}</Button>
          <Button size="small" icon={<TeamOutlined />} onClick={() => openRolesDialog(u)}>{t('user.roles.manage')}</Button>
          <Button size="small" icon={<KeyOutlined />} onClick={() => openResetPw(u)} title={t('user.pw.reset')} />
          {canLockPw && (
            <Button size="small" danger icon={<StopOutlined />} onClick={() => doLockPass(u)}
                    title={t('user.pw.lockTip')} />
          )}
          {u.IsActive
            ? <Button size="small" icon={<LockOutlined />} danger
                      onClick={() => modal.confirm({
                        title:       t('user.lockConfirmTitle'),
                        content:     t('user.lockConfirmContent', { username: u.Username }),
                        icon:        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
                        okText:      t('common.lock'),
                        okButtonProps: { danger: true },
                        cancelText:  t('common.cancel'),
                        onOk:        () => api.lockUser(u.UserID, 'Admin lock').then(refresh),
                      })}>
                {t('common.lock')}
              </Button>
            : <Button size="small" icon={<UnlockOutlined />} onClick={() => api.unlockUser(u.UserID).then(refresh)}>{t('common.unlock')}</Button>
          }
        </span>
      ),
    },
  ];

  return (
    <Card size="small" extra={
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
        {t('common.createNew')}
      </Button>
    }>
      <Table dataSource={users} columns={columns} loading={loading} rowKey="UserID" size="small" bordered
             pagination={{
               pageSize,
               showSizeChanger: true,
               pageSizeOptions: GRID_PAGE_SIZE_OPTIONS,
               onShowSizeChange: (_, size) => setPageSize(size),
             }}
             scroll={{ y: 'calc(100vh - 200px)' }} />

      <Modal
        open={createOpen}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        title={<span><UserAddOutlined /> {t('user.title')}</span>}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical" onFinish={submitCreate} requiredMark={false} autoComplete="off">
          <Form.Item label={t('user.username')} name="Username"
                     rules={[{ required: true, min: 3, max: 100 }]}>
            <Input autoFocus autoComplete="off" />
          </Form.Item>
          <Form.Item label={t('user.fullName')} name="FullName"
                     rules={[{ max: 200 }]}>
            <Input autoComplete="off" placeholder={t('user.fullNamePlaceholder')} />
          </Form.Item>
          <Form.Item label={t('user.email')} name="Email"
                     rules={[{ type: 'email' }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item label={t('user.initialPassword')} name="InitialPassword"
                     rules={[{ required: true, min: MIN_PW }]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <PasswordStrengthIndicator password={newPw} minLength={MIN_PW} />
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Button onClick={() => { setCreateOpen(false); createForm.resetFields(); }} style={{ marginRight: 8 }}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={createBusy} disabled={!pwValid}>
              {t('common.create')}
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={editing !== null}
        onCancel={() => setEditing(null)}
        title={<span><EditOutlined /> {t('user.title')}</span>}
        footer={null}
        width={480}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" onFinish={submitEdit} requiredMark={false} autoComplete="off">
          <Form.Item label={t('user.username')}>
            <Input value={editing?.Username ?? ''} disabled />
          </Form.Item>
          <Form.Item label={t('user.fullName')} name="FullName" rules={[{ max: 200 }]}>
            <Input autoComplete="off" placeholder={t('user.fullNamePlaceholder')} />
          </Form.Item>
          <Form.Item label={t('user.email')} name="Email" rules={[{ type: 'email' }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Button onClick={() => setEditing(null)} style={{ marginRight: 8 }}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={editBusy}>
              {t('common.save')}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Outer: list user's role assignments + button "Assign new"
          Inner RoleAssignmentDialog dùng zIndex CAO HƠN tuyệt đối (1100 > 1000)
          để tránh bug AntD nested-Modal counter — lần 2+ inner KHÔNG bị tụt
          xuống dưới outer dù counter của AntD đã bump. */}
      <Modal
        open={rolesUser !== null}
        onCancel={() => { setRolesUser(null); setUserRoles([]); }}
        title={<span><TeamOutlined /> {t('user.roles.title', { username: rolesUser?.Username ?? '' })}</span>}
        footer={null}
        width={780}
        zIndex={ROLES_MODAL_Z}
        destroyOnHidden
      >
        <Space style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { loadShips(); setAssignOpen(true); }}>
            {t('user.roles.assignNew')}
          </Button>
        </Space>
        <Table
          dataSource={userRoles}
          rowKey="UserRoleID"
          size="small"
          bordered
          loading={userRolesLoading}
          pagination={false}
          locale={{ emptyText: t('user.roles.none') }}
          columns={[
            { title: t('common.id'),    dataIndex: 'UserRoleID', width: 70 },
            { title: t('role.label'),   dataIndex: 'RoleName',
              render: (v, r) => {
                const desc = roleDescById.get(r.RoleID);
                return <span><strong>{v}</strong>{desc ? <span style={{ opacity: 0.55 }}> — {desc}</span> : null}</span>;
              } },
            { title: t('scope.type'),   dataIndex: 'ScopeType',  width: 140,
              render: (v) => <Tag color={v === 'Global' ? 'red' : v === 'Tenant' ? 'blue' : 'cyan'}>{v}</Tag> },
            { title: t('permission.scopeDetails'), key: 'sv',
              // Global/Tenant = toàn bộ tàu (không kèm danh sách). Vessel: neo bằng ShipGuid nằm ở
              // ScopeItems (ScopeValues int luôn rỗng ở FMS) -> dịch GUID sang tên tàu.
              render: (_, r) => {
                if (r.ScopeType === 'Global' || r.ScopeType === 'Tenant')
                  return <span style={{ opacity: 0.4 }}>–</span>;
                const ids = (r.ScopeItems?.length ? r.ScopeItems : (r.ScopeValues ?? [])).map(String);
                if (!ids.length) return <span style={{ opacity: 0.4 }}>–</span>;
                return ids.map((id) => (
                  <Tag key={id} color="geekblue">{shipNameById.get(id.toLowerCase()) ?? id}</Tag>
                ));
              } },
            { title: t('role.expires'), dataIndex: 'ExpiresAtUTC', width: 160,
              render: (v) => v ? new Date(v).toLocaleString() : <span style={{ opacity: 0.4 }}>–</span> },
            { title: t('common.actions'), key: 'a', width: 90,
              render: (_, r) => (
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeRole(r)} />
              ) },
          ]}
        />
      </Modal>

      <Modal
        open={!!pwUser}
        onCancel={() => setPwUser(null)}
        onOk={() => pwForm.submit()}
        confirmLoading={pwSaving}
        forceRender
        title={`${t('user.pw.reset')} — ${pwUser?.Username ?? ''}`}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        <Form form={pwForm} layout="vertical" onFinish={submitResetPw} autoComplete="off">
          <Form.Item
            name="NewPassword"
            label={t('user.pw.new')}
            extra={t('user.pw.help')}
            rules={[{ required: true }, { min: 12, message: t('user.pw.tooShort') }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="Confirm"
            label={t('user.pw.confirm')}
            dependencies={['NewPassword']}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator: (_, v) => (!v || v === getFieldValue('NewPassword'))
                  ? Promise.resolve() : Promise.reject(new Error(t('user.pw.mismatch'))),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      <RoleAssignmentDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        roles={rolesForAssign}
        units={{ Vessel: ships, Department: [] }}
        onConfirm={submitAssign}
        zIndex={ROLES_ASSIGN_DLG_Z}
      />
    </Card>
  );
}
